export const TASK_STATUS = Object.freeze({
    NEW: "NEW",
    ACCEPTED: "ACCEPTED",
    IN_PROGRESS: "IN_PROGRESS",
    WAITING: "WAITING",
    BLOCKED: "BLOCKED",
    IN_REVIEW: "IN_REVIEW",
    CHANGES_REQUESTED: "CHANGES_REQUESTED",
    PAUSED: "PAUSED",
    DONE: "DONE",
    CANCELLED: "CANCELLED",
});

export const DEFAULT_TASK_FEATURES = Object.freeze({
    version: 1,
    taskTypes: Object.freeze({
        delivery: true,
        approval: true,
        recurring: true,
    }),
    work: Object.freeze({
        subtasks: true,
        checklist: true,
        deliverables: true,
    }),
    collaboration: Object.freeze({
        watchers: true,
        verifier: true,
    }),
    planning: Object.freeze({
        reminders: true,
        projectContext: true,
        tags: true,
        timeTracking: true,
    }),
    advanced: Object.freeze({
        manyCreate: true,
        aiAssistant: true,
    }),
});

function normalizeTaskFeatureGroup(value, defaults) {
    const source = value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
    return Object.fromEntries(Object.entries(defaults).map(([key, fallback]) => ([
        key,
        typeof source[key] === "boolean" ? source[key] : fallback,
    ])));
}

export function normalizeTaskFeatures(value) {
    const source = value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
    return {
        version: DEFAULT_TASK_FEATURES.version,
        taskTypes: normalizeTaskFeatureGroup(source.taskTypes, DEFAULT_TASK_FEATURES.taskTypes),
        work: normalizeTaskFeatureGroup(source.work, DEFAULT_TASK_FEATURES.work),
        collaboration: normalizeTaskFeatureGroup(source.collaboration, DEFAULT_TASK_FEATURES.collaboration),
        planning: normalizeTaskFeatureGroup(source.planning, DEFAULT_TASK_FEATURES.planning),
        advanced: normalizeTaskFeatureGroup(source.advanced, DEFAULT_TASK_FEATURES.advanced),
    };
}

export function isTaskFeatureEnabled(features, path) {
    const [group, key] = String(path || "").split(".");
    const normalized = normalizeTaskFeatures(features);
    return Boolean(group && key && normalized[group]?.[key]);
}

export function getTaskFeaturesForDepartment(taskHomeMeta, departmentId) {
    const configured = departmentId
        ? taskHomeMeta?.taskFeatures?.byDepartmentId?.[String(departmentId)]
        : null;
    return normalizeTaskFeatures(configured || taskHomeMeta?.taskFeatures?.defaults);
}

export const STATUS_META = Object.freeze({
    [TASK_STATUS.NEW]: {
        label: "New",
        description: "New work that needs triage",
        color: "#374151",
        background: "#F3F4F6",
    },
    [TASK_STATUS.ACCEPTED]: {
        label: "Accepted",
        description: "A contributor has accepted the task",
        color: "#4B5563",
        background: "#F3F4F6",
    },
    [TASK_STATUS.IN_PROGRESS]: {
        label: "In Progress",
        description: "Work has started",
        color: "#1B1B1B",
        background: "#FFF7D1",
    },
    [TASK_STATUS.WAITING]: {
        label: "Waiting",
        description: "Waiting for someone else or an external response",
        color: "#7A4F00",
        background: "#FFF1B8",
    },
    [TASK_STATUS.BLOCKED]: {
        label: "Blocked",
        description: "Cannot move until a blocker is resolved",
        color: "#991B1B",
        background: "#FEE2E2",
    },
    [TASK_STATUS.IN_REVIEW]: {
        label: "In Review",
        description: "Ready for review or approval",
        color: "#7A4F00",
        background: "#FFF1B8",
    },
    [TASK_STATUS.CHANGES_REQUESTED]: {
        label: "Changes Requested",
        description: "Returned for corrections",
        color: "#9A3412",
        background: "#FFEDD5",
    },
    [TASK_STATUS.PAUSED]: {
        label: "Paused",
        description: "Intentionally parked for later",
        color: "#4B5563",
        background: "#E5E7EB",
    },
    [TASK_STATUS.DONE]: {
        label: "Done",
        description: "Task is complete",
        color: "#166534",
        background: "#DCFCE7",
    },
    [TASK_STATUS.CANCELLED]: {
        label: "Cancelled",
        description: "Task was cancelled",
        color: "#525252",
        background: "#E5E5E5",
    },
});

export const TASK_STATUS_CHANGE_HEADING = "Change task status to:";
export const TASK_STATUS_CHANGE_HEADING_KEY = "__task_status_change_heading";

export const TERMINAL_STATUSES = Object.freeze([
    TASK_STATUS.DONE,
    TASK_STATUS.CANCELLED,
]);

export const COMPLETION_STATUSES = Object.freeze([
    TASK_STATUS.DONE,
    "FINISHED",
    "COMPLETED",
]);

export const APPROVAL_PENDING_STATUSES = Object.freeze([
    TASK_STATUS.IN_REVIEW,
]);

export function normalizeTaskStatus(status) {
    if (!status) {
        return TASK_STATUS.ACCEPTED;
    }
    const normalized = String(status).trim();
    const lifecycleStatus = normalized.replace(/[\s-]+/g, "_").toUpperCase();
    return STATUS_META[lifecycleStatus] ? lifecycleStatus : normalized;
}

export function getTaskStatusMeta(status) {
    const normalized = normalizeTaskStatus(status);
    return STATUS_META[normalized] || {
        label: normalized.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()),
        description: "Custom task status",
        color: "#4B5563",
        background: "#F3F4F6",
    };
}

export function getTaskStatusOptionLifecycleValue(option) {
    if (option && typeof option === "object") {
        return option.globalStatus
            || option.global_status
            || option.value
            || option.key
            || option.name
            || null;
    }
    return option || null;
}

export function getTaskStatusOptionLabel(option) {
    const lifecycleValue = String(getTaskStatusOptionLifecycleValue(option) || "").trim().toUpperCase();
    if (STATUS_META[lifecycleValue]) return STATUS_META[lifecycleValue].label;
    if (option && typeof option === "object") return option.label || option.displayName || option.name || option.value;
    return getTaskStatusMeta(option).label;
}

export function withTaskStatusOptionLabel(option) {
    if (!option || typeof option !== "object") {
        return {
            value: option,
            label: getTaskStatusOptionLabel(option),
        };
    }
    return {
        ...option,
        label: getTaskStatusOptionLabel(option),
    };
}

export function buildTaskStatusChangeMenuItems(options = [], statusBlockedReasons = {}) {
    return [
        {
            key: TASK_STATUS_CHANGE_HEADING_KEY,
            label: TASK_STATUS_CHANGE_HEADING,
            disabled: true,
            className: "task-status-menu-heading",
        },
        ...options.map((option) => {
            const normalizedOption = withTaskStatusOptionLabel(option);
            const blockedReason = statusBlockedReasons[String(normalizedOption.value)];
            return {
                key: normalizedOption.value,
                label: formatDisabledActionLabel(normalizedOption.label, blockedReason),
                disabled: Boolean(blockedReason),
            };
        }),
    ];
}

export function buildTaskStatusChangeSelectOptions(options = [], statusBlockedReasons = {}) {
    return [
        {
            value: TASK_STATUS_CHANGE_HEADING_KEY,
            label: TASK_STATUS_CHANGE_HEADING,
            disabled: true,
            className: "task-status-menu-heading",
        },
        ...options.map((option) => {
            const normalizedOption = withTaskStatusOptionLabel(option);
            const blockedReason = statusBlockedReasons[String(normalizedOption.value)];
            return blockedReason
                ? { ...normalizedOption, label: formatDisabledActionLabel(normalizedOption.label, blockedReason), disabled: true }
                : normalizedOption;
        }),
    ];
}

export function isTerminalTaskStatus(status) {
    return TERMINAL_STATUSES.includes(status) || TERMINAL_STATUSES.includes(normalizeTaskStatus(status));
}

export function isCompletionTaskStatus(status) {
    const normalized = String(normalizeTaskStatus(status) || "").trim().toUpperCase();
    return COMPLETION_STATUSES.includes(normalized);
}

export function isApprovalPendingStatus(status) {
    return APPROVAL_PENDING_STATUSES.includes(status) || APPROVAL_PENDING_STATUSES.includes(normalizeTaskStatus(status));
}

export function isApprovalTaskType(task, taskType) {
    const rawType = task?.task_type || task?.taskType || taskType;
    return String(rawType || "").trim().toLowerCase() === "approval";
}

export function getTaskRestoreStatus(task, { taskType } = {}) {
    return isApprovalTaskType(task, taskType) ? TASK_STATUS.IN_REVIEW : TASK_STATUS.IN_PROGRESS;
}

function taskIdKey(value) {
    if (value === null || value === undefined || value === "") return null;
    return String(value);
}

function taskIdsMatch(left, right) {
    const leftKey = taskIdKey(left);
    const rightKey = taskIdKey(right);
    return Boolean(leftKey && rightKey && leftKey === rightKey);
}

export function getTaskAssigneeId(task) {
    return getTaskAssigneeIds(task)[0] ?? null;
}

export function getTaskAssigneeIds(task) {
    const ids = [];
    const seen = new Set();
    const addId = (value) => {
        const id = Number(value || 0) || null;
        if (!id || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
    };

    (Array.isArray(task?.assignee_ids) ? task.assignee_ids : []).forEach(addId);
    (Array.isArray(task?.assigneeIds) ? task.assigneeIds : []).forEach(addId);
    (Array.isArray(task?.assignees) ? task.assignees : []).forEach((user) => addId(user.id || user.user_id));
    (Array.isArray(task?.task_assignee) ? task.task_assignee : []).forEach((assignment) => addId(assignment.user_id || assignment.user?.id));
    [
        task?.default_assignee_id,
        task?.default_assignee?.id,
        task?.assignee_id,
        task?.assignee?.id,
    ].forEach(addId);

    return ids;
}

export function getTaskClaimedById(task) {
    return task?.claimed_by_id
        ?? task?.claimedBy?.id
        ?? task?.claimed_by?.id
        ?? null;
}

export function getTaskDelegatedById(task) {
    return task?.delegated_by_id
        ?? task?.delegatedBy?.id
        ?? task?.delegated_by?.id
        ?? null;
}

export function getTaskCreatedById(task) {
    return task?.created_by_id
        ?? task?.createdBy?.id
        ?? task?.created_by?.id
        ?? task?.owner?.id
        ?? null;
}

export function isTaskDelegated(task) {
    return Boolean(
        task?.delegated === true
        || getTaskDelegatedById(task)
    );
}

export function taskHasVerifierTarget(task) {
    return Boolean(
        task?.verifier_id
        || task?.verifier?.id
        || task?.verifier_user_id
        || task?.verifierUser?.id
        || task?.verifier_queue_id
        || task?.verifier_queue?.id
    );
}

function getTaskVerifierId(task) {
    return task?.verifier_id
        ?? task?.verifier?.id
        ?? task?.verifier_user_id
        ?? task?.verifier_user?.id
        ?? task?.verifierUser?.id
        ?? null;
}

export function getTaskRelationshipLabel(task, { currentUserId } = {}) {
    const currentUserKey = taskIdKey(currentUserId);
    if (!currentUserKey) return "Involved";

    const assigneeKeys = getTaskAssigneeIds(task).map(taskIdKey);
    const delegatedByKey = taskIdKey(getTaskDelegatedById(task));
    const verifierKey = taskIdKey(getTaskVerifierId(task));
    const claimedByKey = taskIdKey(getTaskClaimedById(task));

    if (delegatedByKey && delegatedByKey === currentUserKey && assigneeKeys.some((key) => key !== currentUserKey)) {
        return "Delegated by me";
    }
    if (delegatedByKey && delegatedByKey !== currentUserKey && assigneeKeys.includes(currentUserKey)) {
        return "Delegated to me";
    }
    if (verifierKey === currentUserKey || (task?.verifier_queue_id && isApprovalPendingStatus(task?.status))) {
        return "Review needed";
    }
    if (claimedByKey === currentUserKey) {
        return "Claimed";
    }
    if (assigneeKeys.includes(currentUserKey)) {
        return "Assigned to me";
    }
    return "Involved";
}

export function getTaskWorkflowStateLabel(task) {
    const status = normalizeTaskStatus(task?.status);
    if (isTerminalTaskStatus(status)) return status === TASK_STATUS.DONE ? "Done" : "Cancelled";
    if (status === TASK_STATUS.IN_REVIEW || isApprovalPendingStatus(status)) return "Ready for review";
    if (status === TASK_STATUS.CHANGES_REQUESTED) return "Changes requested";
    if (taskHasVerifierTarget(task)) return "Waiting for assignee";
    return null;
}

function normalizeTaskDepartment(department, reason = null) {
    const value = department?.department || department;
    const id = Number(
        value?.id
        ?? department?.department_id
        ?? department?.departmentId
        ?? 0
    ) || null;
    const name = value?.name
        || department?.department_name
        || department?.departmentName
        || null;

    if (!id && !name) return null;
    return {
        ...value,
        id,
        name: name || `Department ${id}`,
        reason: department?.reason || reason || null,
    };
}

export function getTaskDepartmentContext(task, { boardDepartment = null } = {}) {
    const departments = [];
    const seen = new Set();
    const addDepartment = (department, reason = null) => {
        const normalized = normalizeTaskDepartment(department, reason);
        if (!normalized) return null;

        const key = normalized.id
            ? `id:${normalized.id}`
            : `name:${String(normalized.name).trim().toLowerCase()}`;
        if (!seen.has(key)) {
            seen.add(key);
            departments.push(normalized);
        }
        return departments.find((item) => (
            normalized.id ? item.id === normalized.id : item.name === normalized.name
        )) || normalized;
    };

    const boardPrimary = addDepartment(boardDepartment, "board");
    const taskHomeContext = task?.taskHomeContext || task?.task_home_context || {};
    const homePrimary = addDepartment(taskHomeContext.primaryDepartment, "task");

    (Array.isArray(taskHomeContext.departments) ? taskHomeContext.departments : [])
        .forEach((department) => addDepartment(department, department?.reason || "task"));

    addDepartment(task?.default_assignee?.department, "assignee");
    addDepartment(task?.task_queue?.department, "queue");
    addDepartment(task?.checklist?.department, "checklist");

    (Array.isArray(task?.project?.project_department) ? task.project.project_department : [])
        .forEach((department) => addDepartment(department, "project"));

    const primary = boardPrimary || homePrimary || departments[0] || null;
    const related = primary
        ? departments.filter((department) => (
            primary.id ? department.id !== primary.id : department.name !== primary.name
        ))
        : [];

    return { primary, related, departments };
}

function userFirstName(user) {
    const explicit = user?.first_name || user?.firstName;
    if (explicit) return String(explicit).trim();

    const label = user?.name || user?.full_name || user?.fullName || user?.email;
    if (!label) return null;
    return String(label).trim().split(/\s+/)[0] || null;
}

export function getTaskVerifierName(task) {
    const verifierName = userFirstName(task?.verifier || task?.verifier_user || task?.verifierUser);
    if (verifierName) return verifierName;

    const queueName = task?.verifier_queue?.name || task?.verifierQueue?.name;
    if (queueName) return queueName;

    return null;
}

export function getTaskVerificationRequiredLabel(task) {
    if (!taskHasVerifierTarget(task)) return null;
    const verifierName = getTaskVerifierName(task);
    return verifierName ? `Need verification from ${verifierName}` : "Need verification";
}

export function formatDisabledActionLabel(label, reason) {
    return reason ? `${label} - ${reason}` : label;
}

export function isTaskOwnedByCurrentUser(task, { currentUserId } = {}) {
    if (!currentUserId) return false;

    const claimedById = getTaskClaimedById(task);
    if (taskIdKey(claimedById)) {
        return taskIdsMatch(claimedById, currentUserId);
    }

    return getTaskAssigneeIds(task).some((assigneeId) => taskIdsMatch(assigneeId, currentUserId));
}

export function canApproveTask(task, { currentUserId, taskType } = {}) {
    const verifierId = task?.verifier_id || task?.verifier?.id || task?.verifier_user_id || task?.verifierUser?.id;
    const hasVerifierQueue = Boolean(task?.verifier_queue_id || task?.verifier_queue?.id);
    if (verifierId) {
        return Boolean(currentUserId && Number(verifierId) === Number(currentUserId));
    }
    return (
        taskType === "verification" &&
        (hasVerifierQueue || !verifierId)
    );
}

export function isTaskDelegator(task, { currentUserId } = {}) {
    const delegatedById = getTaskDelegatedById(task);
    return Boolean(currentUserId && delegatedById && taskIdsMatch(delegatedById, currentUserId));
}

export function isTaskCreator(task, { currentUserId } = {}) {
    const createdById = getTaskCreatedById(task);
    return Boolean(currentUserId && createdById && taskIdsMatch(createdById, currentUserId));
}

export function isTaskRequesterOrDelegator(task, { currentUserId } = {}) {
    return (
        isTaskCreator(task, { currentUserId })
        || isTaskDelegator(task, { currentUserId })
    );
}

export function canRequestChangesTask(task, { currentUserId, taskType } = {}) {
    if (!isTaskDelegated(task)) return true;
    return (
        isTaskDelegator(task, { currentUserId })
        || canApproveTask(task, { currentUserId, taskType })
    );
}

export function canCancelTask(task, { currentUserId } = {}) {
    const status = normalizeTaskStatus(task?.status);
    if (!currentUserId || isTerminalTaskStatus(status)) return false;
    if (isTaskDelegated(task)) return false;
    if (isTaskRequesterOrDelegator(task, { currentUserId })) return true;
    if (taskHasVerifierTarget(task)) return false;
    return isTaskOwnedByCurrentUser(task, { currentUserId });
}

export function canReopenDoneTask(task, { currentUserId, taskType } = {}) {
    if (!currentUserId || normalizeTaskStatus(task?.status) !== TASK_STATUS.DONE) return false;
    return (
        isTaskOwnedByCurrentUser(task, { currentUserId })
        || isTaskRequesterOrDelegator(task, { currentUserId })
        || canApproveTask(task, { currentUserId, taskType })
    );
}

export function canRestoreCancelledTask(task, { currentUserId, taskType } = {}) {
    if (!currentUserId || normalizeTaskStatus(task?.status) !== TASK_STATUS.CANCELLED) return false;
    if (isTaskRequesterOrDelegator(task, { currentUserId })) return true;
    if (isTaskDelegated(task) || taskHasVerifierTarget(task) || isApprovalTaskType(task, taskType)) return false;
    return isTaskOwnedByCurrentUser(task, { currentUserId });
}

export function canPullBackDelegatedTask(task, { currentUserId } = {}) {
    const status = normalizeTaskStatus(task?.status);
    return Boolean(
        currentUserId
        && isTaskDelegated(task)
        && !isApprovalPendingStatus(status)
        && isTaskRequesterOrDelegator(task, { currentUserId })
    );
}

export function canRequestTaskCancellation(task, { currentUserId } = {}) {
    const status = normalizeTaskStatus(task?.status);
    return Boolean(
        currentUserId
        && !isTerminalTaskStatus(status)
        && isTaskOwnedByCurrentUser(task, { currentUserId })
        && !canCancelTask(task, { currentUserId })
        && (isTaskDelegated(task) || taskHasVerifierTarget(task))
    );
}

export function canStartTask(task, { currentUserId } = {}) {
    const status = normalizeTaskStatus(task?.status);
    return (
        !isTerminalTaskStatus(status)
        && !isCompletionTaskStatus(status)
        && !isApprovalPendingStatus(status)
        && status !== TASK_STATUS.IN_PROGRESS
        && isTaskOwnedByCurrentUser(task, { currentUserId })
    );
}

export function canSendTaskForApproval(task, { currentUserId } = {}) {
    const status = normalizeTaskStatus(task?.status);
    return (
        !isTerminalTaskStatus(status)
        && !isApprovalPendingStatus(status)
        && isTaskOwnedByCurrentUser(task, { currentUserId })
    );
}

export function canCompleteTask(task, { currentUserId, taskType } = {}) {
    const status = normalizeTaskStatus(task?.status);
    if (isTerminalTaskStatus(status)) return false;
    if (
        taskType === "verification"
        || isApprovalTaskType(task, taskType)
        || (isApprovalPendingStatus(status) && taskHasVerifierTarget(task))
    ) {
        return canApproveTask(task, { currentUserId, taskType });
    }
    if (taskHasVerifierTarget(task)) return false;
    return isTaskOwnedByCurrentUser(task, { currentUserId });
}

export function getTaskCompletionBlockedReason(task, { currentUserId, taskType } = {}) {
    if (canCompleteTask(task, { currentUserId, taskType })) return null;
    const verificationRequiredLabel = getTaskVerificationRequiredLabel(task);
    if (verificationRequiredLabel) return verificationRequiredLabel;
    if (isApprovalTaskType(task, taskType) || taskType === "verification") {
        return "Only verifier can approve";
    }
    if (!isTaskOwnedByCurrentUser(task, { currentUserId })) {
        const ownerLabel = getTaskAssigneeIds(task).length > 1 ? "contributor" : "assignee";
        return `Only ${ownerLabel} can complete`;
    }
    return "Task cannot be completed";
}

export function getTaskStatusBlockedReason(task, status, { currentUserId, taskType } = {}) {
    const targetStatus = String(normalizeTaskStatus(status) || "").trim().toUpperCase();
    const currentStatus = String(normalizeTaskStatus(task?.status) || "").trim().toUpperCase();
    if (targetStatus && targetStatus === currentStatus) return null;

    if (isCompletionTaskStatus(targetStatus)) {
        return getTaskCompletionBlockedReason(task, { currentUserId, taskType });
    }

    if (targetStatus === TASK_STATUS.NEW && currentStatus !== TASK_STATUS.NEW) {
        return "Task already accepted";
    }

    if (targetStatus === TASK_STATUS.CANCELLED && currentStatus === TASK_STATUS.DONE) {
        return "Task is already done; re-open it before cancelling";
    }

    if (targetStatus === TASK_STATUS.CANCELLED && !canCancelTask(task, { currentUserId })) {
        if (isTaskDelegated(task)) return "Task is delegated";
        if (taskHasVerifierTarget(task)) return "Only requester or admin can cancel reviewed tasks";
        return "Only assignee, requester, or admin can cancel";
    }

    if (
        targetStatus === TASK_STATUS.CHANGES_REQUESTED
        && isTaskDelegated(task)
        && !canRequestChangesTask(task, { currentUserId, taskType })
    ) {
        return "Only delegator or verifier can request changes";
    }

    if (targetStatus === TASK_STATUS.IN_PROGRESS && currentStatus === TASK_STATUS.DONE) {
        return canReopenDoneTask(task, { currentUserId, taskType })
            ? null
            : "Only assignee, requester, verifier, or admin can re-open";
    }

    if (targetStatus === TASK_STATUS.IN_PROGRESS && currentStatus === TASK_STATUS.CANCELLED) {
        if (isApprovalTaskType(task, taskType)) return "Approval requests restore to review";
        return canRestoreCancelledTask(task, { currentUserId, taskType })
            ? null
            : "Only requester, owner, or admin can restore";
    }

    if (targetStatus === TASK_STATUS.IN_REVIEW) {
        if (currentStatus === TASK_STATUS.CANCELLED) {
            return canRestoreCancelledTask(task, { currentUserId, taskType })
                ? null
                : "Only requester, owner, or admin can restore";
        }

        if (isApprovalTaskType(task, taskType)) {
            return isTaskRequesterOrDelegator(task, { currentUserId }) ? null : "Only requester can send for approval";
        }
        return isTaskOwnedByCurrentUser(task, { currentUserId }) ? null : "Only assignee can send for approval";
    }

    return null;
}

const TEAM_BOARD_MANAGER_START_OVERRIDE_SOURCES = new Set([
    TASK_STATUS.NEW,
    TASK_STATUS.ACCEPTED,
    TASK_STATUS.WAITING,
    TASK_STATUS.BLOCKED,
    TASK_STATUS.CHANGES_REQUESTED,
    TASK_STATUS.PAUSED,
]);

export function getTeamBoardStatusMoveDecision(
    task,
    status,
    { currentUserId, canMoveBoardCards, canManageBoardCards } = {}
) {
    if (!canMoveBoardCards) {
        return {
            blockedReason: "Only team board managers can move cards on this board",
            requiresManagerOverride: false,
        };
    }

    let blockedReason = getTaskStatusBlockedReason(task, status, {
        currentUserId,
        taskType: "team",
    });

    const targetStatus = normalizeTaskStatus(status);
    const currentStatus = normalizeTaskStatus(task?.status);
    const approvalSensitive = taskHasVerifierTarget(task)
        || isApprovalTaskType(task, "team")
        || isApprovalPendingStatus(currentStatus);

    const restrictedCancellation = targetStatus === TASK_STATUS.CANCELLED
        && targetStatus !== currentStatus
        && !isTerminalTaskStatus(currentStatus)
        && !isTaskDelegated(task)
        && Boolean(blockedReason);
    if (restrictedCancellation && canManageBoardCards) {
        return {
            blockedReason: null,
            requiresManagerOverride: true,
        };
    }

    const restrictedCancelledRestore = currentStatus === TASK_STATUS.CANCELLED
        && targetStatus === getTaskRestoreStatus(task, { taskType: "team" })
        && Boolean(blockedReason);
    if (restrictedCancelledRestore && canManageBoardCards && !isTaskDelegated(task)) {
        return {
            blockedReason: null,
            requiresManagerOverride: true,
        };
    }

    if (isCompletionTaskStatus(targetStatus) && !approvalSensitive) {
        blockedReason = null;
    }
    if (
        targetStatus === TASK_STATUS.IN_PROGRESS
        && currentStatus !== TASK_STATUS.CANCELLED
        && !approvalSensitive
    ) {
        blockedReason = null;
    }

    const restrictedStart = targetStatus === TASK_STATUS.IN_PROGRESS
        && targetStatus !== currentStatus
        && ![TASK_STATUS.DONE, TASK_STATUS.CANCELLED].includes(currentStatus)
        && approvalSensitive
        && !isTaskOwnedByCurrentUser(task, { currentUserId });
    if (restrictedStart) {
        const canReturnFromReview = currentStatus === TASK_STATUS.IN_REVIEW
            && canApproveTask(task, { currentUserId, taskType: "team" });
        const requiresManagerOverride = Boolean(
            canManageBoardCards
            && (
                TEAM_BOARD_MANAGER_START_OVERRIDE_SOURCES.has(currentStatus)
                || canReturnFromReview
            )
            && taskHasVerifierTarget(task)
            && !isApprovalTaskType(task, "team")
        );
        return {
            blockedReason: requiresManagerOverride
                ? null
                : (blockedReason || "Only assignee can start this task"),
            requiresManagerOverride,
        };
    }

    return {
        blockedReason,
        requiresManagerOverride: false,
    };
}

export function getTeamBoardStatusBlockedReason(task, status, options = {}) {
    return getTeamBoardStatusMoveDecision(task, status, options).blockedReason;
}

export function nextCompletionStatus(task, { currentUserId, taskType } = {}) {
    const hasVerifierTarget = taskHasVerifierTarget(task);

    if (!hasVerifierTarget || canApproveTask(task, { currentUserId, taskType })) {
        return TASK_STATUS.DONE;
    }

    return TASK_STATUS.IN_REVIEW;
}
